import { authFetch } from '@/lib/api';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowLeft, Check, Edit3, Save, X, Plus, Trash2, Loader2, ChevronDown, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Compute per-section item keys (legacy `items` + new `during_items` + `end_items`).
const sectionItemKeys = (section) => {
    const keys = [];
    (section.items || []).forEach((_, i) => keys.push(`${section.id}::item::${i}`));
    (section.during_items || []).forEach((_, i) => keys.push(`${section.id}::during::${i}`));
    (section.end_items || []).forEach((_, i) => keys.push(`${section.id}::end::${i}`));
    return keys;
};

const isSectionComplete = (section, checked) => {
    const keys = sectionItemKeys(section);
    if (keys.length === 0) return false;
    return keys.every(k => checked[k]);
};

export default function GuideChecklistArriveePage() {
    const navigate = useNavigate();
    const isAdmin = JSON.parse(localStorage.getItem('guide_user') || '{}').role === 'admin';
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [checked, setChecked] = useState({});
    const [editing, setEditing] = useState(false);
    const [editSections, setEditSections] = useState([]);
    const [saving, setSaving] = useState(false);

    const fetchPage = useCallback(async () => {
        try {
            const res = await authFetch(`${API}/api/guide/pages/checklist-arrivee`);
            const data = await res.json();
            setPage(data);
        } catch { toast.error('Erreur de chargement'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchPage(); }, [fetchPage]);

    const toggle = (key) => {
        setChecked(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const sections = useMemo(() => page?.sections || [], [page]);

    const { totalItems, checkedCount, sectionStatus } = useMemo(() => {
        let total = 0;
        let done = 0;
        const status = []; // {complete, unlocked}
        let prevComplete = true;
        sections.forEach((section) => {
            const keys = sectionItemKeys(section);
            total += keys.length;
            const sectionDone = keys.filter(k => checked[k]).length;
            done += sectionDone;
            const complete = keys.length > 0 && sectionDone === keys.length;
            status.push({ complete, unlocked: prevComplete });
            prevComplete = complete;
        });
        return { totalItems: total, checkedCount: done, sectionStatus: status };
    }, [sections, checked]);

    const startEdit = () => {
        setEditSections(JSON.parse(JSON.stringify(sections)));
        setEditing(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await authFetch(`${API}/api/guide/pages/checklist-arrivee`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sections: editSections, title: page?.title, subtitle: page?.subtitle }),
            });
            const data = await res.json();
            setPage(data);
            setEditing(false);
            setChecked({});
            toast.success('Suivi mis a jour');
        } catch { toast.error('Erreur'); }
        finally { setSaving(false); }
    };

    if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

    if (editing) {
        return (
            <EditMode
                editSections={editSections}
                setEditSections={setEditSections}
                onCancel={() => setEditing(false)}
                onSave={handleSave}
                saving={saving}
            />
        );
    }

    return (
        <div className="px-4 py-5" data-testid="guide-checklist-arrivee">
            <button onClick={() => navigate('/guide')} className="flex items-center gap-1 text-sm text-slate-500 mb-4 active:text-slate-700">
                <ArrowLeft className="w-4 h-4" /> Accueil
            </button>

            <div className="flex items-center justify-between mb-4">
                <div>
                    <h1 className="text-xl font-bold text-slate-900">{page?.title || 'Suivi de debut de service'}</h1>
                    <p className="text-sm text-slate-500">{page?.subtitle || 'Guide etape par etape'}</p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-xs font-bold" data-testid="checklist-progress">
                        {checkedCount}/{totalItems}
                    </div>
                    {isAdmin && <Button size="sm" variant="ghost" className="text-slate-400 p-1" onClick={startEdit} data-testid="edit-page-btn">
                        <Edit3 className="w-3.5 h-3.5" />
                    </Button>}
                </div>
            </div>

            <div className="w-full bg-slate-200 rounded-full h-1.5 mb-4">
                <div className="bg-blue-500 h-1.5 rounded-full transition-all duration-300" style={{ width: `${totalItems > 0 ? (checkedCount / totalItems) * 100 : 0}%` }} />
            </div>

            {/* Pipeline of steps with left timeline rail */}
            <div className="relative">
                <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-slate-200" aria-hidden />
                <div className="space-y-3">
                    {sections.map((section, idx) => {
                        const status = sectionStatus[idx] || { complete: false, unlocked: idx === 0 };
                        return (
                            <StepCard
                                key={section.id || idx}
                                section={section}
                                index={idx}
                                status={status}
                                checked={checked}
                                onToggle={toggle}
                            />
                        );
                    })}
                </div>
            </div>

            {checkedCount === totalItems && totalItems > 0 && (
                <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                    <p className="text-emerald-700 font-semibold text-sm">Service pret !</p>
                    <p className="text-emerald-600 text-xs mt-1">Toutes les etapes sont terminees. Bon service.</p>
                </div>
            )}
        </div>
    );
}

// -------- Step Card --------
function StepCard({ section, index, status, checked, onToggle }) {
    const { complete, unlocked } = status;
    const hasZones = (section.during_items?.length > 0) || (section.end_items?.length > 0);
    const grey = !unlocked;

    return (
        <div className={`relative pl-9 transition-opacity ${grey ? 'opacity-60' : ''}`} data-testid={`step-${index}`}>
            {/* Step marker on the rail */}
            <div className={`absolute left-0 top-2 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shadow-sm border ${
                complete
                    ? 'bg-emerald-500 border-emerald-500 text-white'
                    : grey
                        ? 'bg-slate-100 border-slate-300 text-slate-400'
                        : 'bg-blue-500 border-blue-500 text-white'
            }`}>
                {complete ? <Check className="w-3.5 h-3.5" /> : grey ? <Lock className="w-3 h-3" /> : (index + 1)}
            </div>

            <div className={`bg-white rounded-xl border shadow-sm overflow-hidden ${
                complete ? 'border-emerald-200' : grey ? 'border-slate-200 border-dashed' : 'border-blue-200'
            }`}>
                <div className={`px-3 py-2 border-b ${
                    complete ? 'bg-emerald-50 border-emerald-100' : grey ? 'bg-slate-50 border-slate-100' : 'bg-blue-50 border-blue-100'
                }`}>
                    <h2 className={`font-semibold text-sm ${
                        complete ? 'text-emerald-800' : grey ? 'text-slate-500' : 'text-blue-900'
                    }`}>{section.title}</h2>
                </div>

                {/* Items rendering */}
                {hasZones ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-100">
                        <ZoneBlock
                            label={section.during_label || 'Pendant'}
                            items={section.during_items || []}
                            sectionId={section.id}
                            kind="during"
                            checked={checked}
                            onToggle={onToggle}
                            accent="amber"
                        />
                        <ZoneBlock
                            label={section.end_label || 'En fin'}
                            items={section.end_items || []}
                            sectionId={section.id}
                            kind="end"
                            checked={checked}
                            onToggle={onToggle}
                            accent="purple"
                        />
                    </div>
                ) : (
                    <FlatItems
                        items={section.items || []}
                        sectionId={section.id}
                        checked={checked}
                        onToggle={onToggle}
                    />
                )}
            </div>
        </div>
    );
}

function FlatItems({ items, sectionId, checked, onToggle }) {
    if (!items.length) return null;
    return (
        <div className="divide-y divide-slate-100">
            {items.map((item, j) => {
                const key = `${sectionId}::item::${j}`;
                const isChecked = !!checked[key];
                return (
                    <button key={j} onClick={() => onToggle(key)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors active:bg-slate-50 ${isChecked ? 'bg-blue-50/30' : ''}`}
                        data-testid={`item-${sectionId}-${j}`}>
                        <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors ${isChecked ? 'bg-blue-500 border-blue-500' : 'border-2 border-slate-300'}`}>
                            {isChecked && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <span className={`text-xs ${isChecked ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{item}</span>
                    </button>
                );
            })}
        </div>
    );
}

function ZoneBlock({ label, items, sectionId, kind, checked, onToggle, accent }) {
    if (!items.length) {
        return <div className="px-3 py-2 text-[10px] uppercase tracking-wide text-slate-300">{label}</div>;
    }
    const accentClass = accent === 'amber'
        ? 'bg-amber-50 text-amber-700 border-amber-200'
        : 'bg-purple-50 text-purple-700 border-purple-200';
    return (
        <div className="px-3 py-2">
            <div className={`inline-block text-[10px] uppercase tracking-wide font-bold rounded px-1.5 py-0.5 border mb-1.5 ${accentClass}`}>
                {label}
            </div>
            <div className="space-y-1">
                {items.map((item, j) => {
                    const key = `${sectionId}::${kind}::${j}`;
                    const isChecked = !!checked[key];
                    return (
                        <button key={j} onClick={() => onToggle(key)}
                            className={`w-full flex items-start gap-2 py-1 text-left transition-colors active:bg-slate-50 ${isChecked ? 'opacity-60' : ''}`}
                            data-testid={`zone-${kind}-${sectionId}-${j}`}>
                            <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${isChecked ? 'bg-blue-500 border-blue-500' : 'border-2 border-slate-300'}`}>
                                {isChecked && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <span className={`text-xs leading-snug ${isChecked ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{item}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// -------- Edit Mode --------
function EditMode({ editSections, setEditSections, onCancel, onSave, saving }) {
    const setSection = (idx, updates) => {
        setEditSections(prev => {
            const c = JSON.parse(JSON.stringify(prev));
            c[idx] = { ...c[idx], ...updates };
            return c;
        });
    };
    const addSection = () => {
        setEditSections(prev => [...prev, { id: '', title: '', items: [] }]);
    };
    const removeSection = (idx) => {
        setEditSections(prev => prev.filter((_, i) => i !== idx));
    };
    const moveSection = (idx, dir) => {
        setEditSections(prev => {
            const c = [...prev];
            const target = idx + dir;
            if (target < 0 || target >= c.length) return c;
            [c[idx], c[target]] = [c[target], c[idx]];
            return c;
        });
    };

    const addListItem = (sIdx, listKey) => {
        setEditSections(prev => {
            const c = JSON.parse(JSON.stringify(prev));
            if (!Array.isArray(c[sIdx][listKey])) c[sIdx][listKey] = [];
            c[sIdx][listKey].push('');
            return c;
        });
    };
    const updateListItem = (sIdx, listKey, iIdx, value) => {
        setEditSections(prev => {
            const c = JSON.parse(JSON.stringify(prev));
            c[sIdx][listKey][iIdx] = value;
            return c;
        });
    };
    const removeListItem = (sIdx, listKey, iIdx) => {
        setEditSections(prev => {
            const c = JSON.parse(JSON.stringify(prev));
            c[sIdx][listKey].splice(iIdx, 1);
            return c;
        });
    };

    const toggleZonesMode = (sIdx, on) => {
        setEditSections(prev => {
            const c = JSON.parse(JSON.stringify(prev));
            if (on) {
                c[sIdx].during_items = c[sIdx].during_items || [];
                c[sIdx].end_items = c[sIdx].end_items || [];
                c[sIdx].during_label = c[sIdx].during_label || 'Pendant';
                c[sIdx].end_label = c[sIdx].end_label || 'En fin';
            } else {
                delete c[sIdx].during_items;
                delete c[sIdx].end_items;
                delete c[sIdx].during_label;
                delete c[sIdx].end_label;
            }
            return c;
        });
    };

    return (
        <div className="px-4 py-5" data-testid="guide-checklist-arrivee-edit">
            <h2 className="text-base font-bold text-slate-900 mb-3">Modifier le suivi de debut de service</h2>
            <div className="space-y-3">
                {editSections.map((section, si) => {
                    const hasZones = Array.isArray(section.during_items) || Array.isArray(section.end_items);
                    return (
                        <div key={si} className="bg-white rounded-xl border border-slate-200 p-3" data-testid={`edit-section-${si}`}>
                            <div className="flex items-center gap-2 mb-2">
                                <Input value={section.title || ''} onChange={e => setSection(si, { title: e.target.value })} placeholder="Etape..." className="h-8 text-sm font-semibold flex-1" />
                                <button onClick={() => moveSection(si, -1)} className="text-slate-400 hover:text-slate-700" title="Monter"><ChevronDown className="w-4 h-4 rotate-180" /></button>
                                <button onClick={() => moveSection(si, 1)} className="text-slate-400 hover:text-slate-700" title="Descendre"><ChevronDown className="w-4 h-4" /></button>
                                <button onClick={() => removeSection(si)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                            </div>
                            <label className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                                <input
                                    type="checkbox"
                                    checked={hasZones}
                                    onChange={(e) => toggleZonesMode(si, e.target.checked)}
                                    data-testid={`edit-zones-toggle-${si}`}
                                />
                                Mode 2 zones (Pendant / En fin)
                            </label>

                            {hasZones ? (
                                <div className="space-y-3">
                                    <ZoneEditor
                                        label={section.during_label || 'Pendant'}
                                        labelKey="during_label"
                                        listKey="during_items"
                                        items={section.during_items || []}
                                        sIdx={si}
                                        setSection={setSection}
                                        addListItem={addListItem}
                                        updateListItem={updateListItem}
                                        removeListItem={removeListItem}
                                    />
                                    <ZoneEditor
                                        label={section.end_label || 'En fin'}
                                        labelKey="end_label"
                                        listKey="end_items"
                                        items={section.end_items || []}
                                        sIdx={si}
                                        setSection={setSection}
                                        addListItem={addListItem}
                                        updateListItem={updateListItem}
                                        removeListItem={removeListItem}
                                    />
                                </div>
                            ) : (
                                <div className="space-y-1 pl-2">
                                    {(section.items || []).map((item, ii) => (
                                        <div key={ii} className="flex items-center gap-2">
                                            <Input value={item} onChange={e => updateListItem(si, 'items', ii, e.target.value)} placeholder="Sous-tache..." className="h-7 text-xs flex-1" />
                                            <button onClick={() => removeListItem(si, 'items', ii)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                                        </div>
                                    ))}
                                    <Button variant="ghost" size="sm" className="text-xs text-slate-400 w-full" onClick={() => addListItem(si, 'items')}>
                                        <Plus className="w-3 h-3 mr-1" />Sous-tache
                                    </Button>
                                </div>
                            )}
                        </div>
                    );
                })}
                <Button variant="outline" size="sm" className="w-full" onClick={addSection}>
                    <Plus className="w-4 h-4 mr-1" />Etape
                </Button>
                <div className="flex gap-2 pt-2">
                    <Button variant="outline" className="flex-1" onClick={onCancel}><X className="w-4 h-4 mr-1" />Annuler</Button>
                    <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={onSave} disabled={saving} data-testid="save-page-btn">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}Sauvegarder
                    </Button>
                </div>
            </div>
        </div>
    );
}

function ZoneEditor({ label, labelKey, listKey, items, sIdx, setSection, addListItem, updateListItem, removeListItem }) {
    return (
        <div className="border border-dashed border-slate-200 rounded-lg p-2">
            <Input
                value={label}
                onChange={(e) => setSection(sIdx, { [labelKey]: e.target.value })}
                placeholder="Libelle de la zone"
                className="h-7 text-xs font-semibold mb-1.5"
            />
            <div className="space-y-1 pl-2">
                {items.map((item, ii) => (
                    <div key={ii} className="flex items-center gap-2">
                        <Input value={item} onChange={e => updateListItem(sIdx, listKey, ii, e.target.value)} placeholder="Sous-tache..." className="h-7 text-xs flex-1" />
                        <button onClick={() => removeListItem(sIdx, listKey, ii)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                    </div>
                ))}
                <Button variant="ghost" size="sm" className="text-xs text-slate-400 w-full" onClick={() => addListItem(sIdx, listKey)}>
                    <Plus className="w-3 h-3 mr-1" />Sous-tache
                </Button>
            </div>
        </div>
    );
}
